import * as React from 'react';

import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

const getOnLineStatus = (): boolean =>
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
        ? navigator.onLine
        : true;

export const useNavigatorOnLine = () => {
    const [status, setStatus] = React.useState<boolean>(getOnLineStatus());

    const setOnline = () => setStatus(true);
    const setOffline = () => setStatus(false);

    React.useEffect(() => {
        window.addEventListener('online', setOnline);
        window.addEventListener('offline', setOffline);

        return () => {
            window.removeEventListener('online', setOnline);
            window.removeEventListener('offline', setOffline);
        };
    }, []);

    return status;
};

const NetworkStatusIndicator = (): null => {
    const isOnline: boolean = useNavigatorOnLine();
    const firstUpdate = React.useRef(true);

    React.useLayoutEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (isOnline) {
            toast.clearWaitingQueue();
            toast.dismiss();
        }
        else if (!isOnline) {
            toast.warning('You are currently offline', {
                autoClose: false, closeOnClick: false, closeButton: false, draggable: false
            });
        }
    }, [isOnline]);

    return null;
};

export const useRouteHistory = (): string[] => {
    const location = useLocation();
    const [history, setHistory] = React.useState<string[]>([]);

    React.useEffect(() => {
        setHistory((prevHistory) => {
            if (!prevHistory.includes(location.pathname)) {
                return [...prevHistory, location.pathname];
            }
            return prevHistory;
        });
    }, [location]);

    return history;
};

export default NetworkStatusIndicator;